"use client";

import {
  Button,
  Form,
  FormInput,
  FormPasswordInput,
} from "@ac-expo/ui-components";
import { useForm } from "react-hook-form";
import {
  defaultLoginValues,
  LoginForm,
  LoginSchema,
} from "../../schema/auth.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { getMyUser, userQueryKeys } from "@ac-expo/api";
import { AuthErrorCode } from "@ac-expo/api-types";
import { useQueryClient } from "@tanstack/react-query";
import { syncGuestCartToServer } from "@ac-expo/hooks";
import { parseAuthError } from "@ac-expo/utils";
import { AuthModalPossibilities } from "@web-app/src/types/authModalPossibilities";
import { useState } from "react";
import { useRouter } from "next/router";
import OwnImage from "@ui/components/ui/event/OwnImage";
import mailConfirmationStore from "@web-app/src/services/MailConfirmationStore";
import { GoogleLoginButton } from "./GoogleLoginButton";
import { AppleLoginButton } from "./AppleLoginButton";
import { signInWithCredentials } from "@web-app/src/services/credentials-sign-in";
import {
  CASH_REGISTER_ROUTE,
  isCashRegisterHolder,
} from "@web-app/src/services/cash-register-routing";
import { stripAuthModalParams } from "@web-app/src/services/sso-callback";
import { isAxiosError } from "axios";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import { useCartContext } from "@web-app/src/context/cart.context";

type Props = {
  onSwitchSteps: (step: AuthModalPossibilities) => void;
  onClose: () => void;
  baseEmail?: string | null;
  goto?: string | null;
};

export function Login({ onSwitchSteps, onClose, baseEmail, goto }: Props) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { refetchSession } = useUserContext();
  const { isOpen: isCartOpen } = useCartContext();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ssoRedirectPath = goto ?? stripAuthModalParams(router.asPath);

  const form = useForm<LoginForm>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      ...defaultLoginValues,
      ...(baseEmail ? { emailOrConnectId: baseEmail } : {}),
    },
  });

  const setAuthError = (rawMessage: string | null | undefined) => {
    const { message, code } = parseAuthError(
      rawMessage ?? "Une erreur est survenue"
    );
    setErrorMessage(message);
    setErrorCode(code);
  };

  const handleSubmit = async (values: LoginForm) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    setErrorCode(null);

    try {
      const signInResult = await signInWithCredentials(values);

      if (!signInResult.ok) {
        setErrorMessage(signInResult.message);
        setErrorCode(signInResult.code);
        return;
      }

      refetchSession();
      await syncGuestCartToServer("store");

      // Fetched here rather than awaited from the provider's refetch, so a cash
      // register holder lands on the counter without flashing the current page.
      // The sign-in already succeeded: a failure here must not look like a failed
      // login, and useRedirectCashRegisterHolder still catches a holder later.
      const user = await queryClient
        .fetchQuery({ queryKey: userQueryKeys.me(), queryFn: getMyUser })
        .catch(() => null);

      onClose();

      if (isCashRegisterHolder(user?.userRole)) {
        await router.replace(CASH_REGISTER_ROUTE);

        return;
      }

      if (goto) {
        router.push(goto);
      }
    } catch (err) {
      const rawMessage = isAxiosError(err) ? err.response?.data?.message : null;
      setAuthError(rawMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 ">
        <div className="flex flex-col items-center gap-2">
          <OwnImage
            src="/images/logos/logo_connect_events_vertical.png"
            alt="logo avec nom Connect'Events vertical"
            className="static h-28 w-auto"
          />
        </div>
        <div className="text-center">
          <h1 className="text-xl font-extrabold text-black">Se connecter</h1>
        </div>
        <div className="flex flex-col gap-2">
          <GoogleLoginButton
            onSuccess={onClose}
            redirectPath={ssoRedirectPath}
            openCart={isCartOpen}
          />
          <AppleLoginButton
            onSuccess={onClose}
            redirectPath={ssoRedirectPath}
            openCart={isCartOpen}
          />
        </div>
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-muted-foreground">ou</span>
          </div>
        </div>
        <div className="space-y-4">
          <FormInput
            className="w-full"
            control={form.control}
            name="emailOrConnectId"
            label="Email ou identifiant Connect'Events"
            placeholder="Saisissez votre adresse mail ou identifiant Connect'Events"
          />
          <FormPasswordInput
            className="grow"
            control={form.control}
            name="password"
            label="Mot de passe"
            placeholder="............."
            onChange={(e) => {
              form.setValue("password", e.target.value);
              form.trigger("password");
            }}
            hasMessage={false}
          />
        </div>
        {errorMessage && (
          <div className="space-y-2">
            <p className="text-destructive">{errorMessage}</p>
            {errorCode === AuthErrorCode.INVALID_CREDENTIALS && (
              <em className="block text-sm text-muted-foreground">
                Si vous vous êtes inscrit avec Google ou Apple, utilisez le
                bouton correspondant ci-dessus, ou cliquez sur &quot;Mot de
                passe oublié&quot; pour définir un mot de passe.
              </em>
            )}
            {errorCode === AuthErrorCode.EMAIL_NOT_VERIFIED && (
              <Button
                variant="outline-white"
                className="text-destructive"
                onClick={() => {
                  mailConfirmationStore.setUserEmail(
                    form.getValues().emailOrConnectId
                  );
                  mailConfirmationStore.setModalVisible(true);
                }}
              >
                Vérifier l&apos;email
              </Button>
            )}
          </div>
        )}
        <div className="flex w-full justify-end">
          <Button
            type="button"
            className="text-sm text-gray-500 underline"
            variant="null"
            onClick={() => onSwitchSteps("RESETPASSWORD")}
          >
            Mot de passe oublié
          </Button>
        </div>
        <div className="flex w-full flex-col gap-1">
          <Button
            type="submit"
            variant="blue"
            size="lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Connexion..." : "Se connecter"}
          </Button>
          <Button
            type={"button"}
            className="border-none shadow-none"
            size="lg"
            onClick={() => {
              // @ts-expect-error window.gtag is defined
              if (typeof window !== "undefined" && window.gtag) {
                // @ts-expect-error gtag is defined
                window.gtag("event", "sign up button click", {
                  event_category: "Sign up",
                  event_label: "Opened sign up modal",
                });
              }
              onSwitchSteps("SIGNUP");
            }}
          >
            Créer un compte
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default Login;
